<template>
  <div>
    <loader v-if="is.loading || !orderDataLoaded" />
    <template v-else>
      <template v-if="orderlyTasks.length > 0">
        <h4>Offene Aufgaben</h4>
        <table class="table table-bordered">
          <tbody>
            <tr>
              <th>Zu prüfende Produktanfragen</th>
              <td>
                <div class="status-group mb-2">
                  <template v-if="productTasks.length > 0">
                    <template v-for="(task) in productTasks">
                      <task-item :key="task.id"
                                 :task="task"
                                 :order="entity"
                                 :additional-text="getProductDescription(task.extractId, task.searchId)"
                                 @task-resolved="onTaskResolved" />
                    </template>
                  </template>
                  <template v-else>
                    <status-badge icon="b-check-circle-fill" variant="success" title="Produktzuweisungen komplett" position="single">
                      Produktzuweisungen komplett
                    </status-badge>
                  </template>
                </div>
                <b-link :to="{ name: 'Clarli.Orders.Detail', params: { orderid: eParent.id } }"><b-icon icon="arrow-right" /> zu den bestellten Produkten</b-link>
              </td>
            </tr>
            <tr>
              <th>Zu prüfende Adressierung</th>
              <td>
                <div class="status-group mb-2">
                  <template v-if="locationTasks.length > 0">
                    <template v-for="(task) in locationTasks">
                      <task-item :key="task.id"
                                 :task="task"
                                 :order="entity"
                                 @task-resolved="onTaskResolved" />
                    </template>
                  </template>
                  <template v-else>
                    <status-badge icon="b-check-circle-fill" variant="success" title="Adressierung der Bestellung komplett" position="single">
                      Adressierung der Bestellung komplett
                    </status-badge>
                  </template>
                </div>

                <b-link :to="{ name: 'Clarli.Orders.Addressing', params: { orderid: eParent.id } }"><b-icon icon="arrow-right" /> zur Adressierung</b-link>
              </td>
            </tr>
          </tbody>
        </table>
      </template>

      <h4>Eingangsdaten</h4>
      <div class="alert alert-info">
        <h5>Nachricht</h5>
        <div v-html="renderedMessage" class="markdown-content"></div>
      </div>
      <table class="table table-bordered">
        <colgroup>
          <col style="width: 33%;">
          <col style="width: 33%;">
          <col style="width: 33%;">
        </colgroup>
        <tbody>
          <tr>
            <th>Anhang</th>
            <th>Eingangsdaten</th>
            <th>Visualisierung</th>
          </tr>
          <tr v-for="(attachment, index) in visibleAttachments" :key="index">
            <td>
              <template v-if="attachment.processedAttachmentType === 'OcrResult'">
                Vorlage: {{ attachment.referenceId }}<br />
                <image-lightbox :image-url="`${baseUrl}/order/api/templates/${attachment.referenceId}/visualization/withauth/${templateAuthKey}`" :preview-height="250"
                                alt="Template preview" />
                <br />
              </template>
              Dateiname: {{ attachment.name }}
            </td>
            <td class="text-center">
              <template v-if="attachment.processedAttachmentType === 'OcrResult'">
                <image-lightbox :image-url="`${baseUrl}/order/api/parseorder/${attachment.id}/input/withauth/${parseOrderAuthKey}`" :preview-height="250"
                                alt="Input preview" />
              </template>
              <template v-else>
                <image-lightbox v-if="attachment.id" :image-url="`${baseUrl}/order/api/parseorder/${attachment.id}/input/withauth/${parseOrderAuthKey}`"
                                :preview-height="250"
                                alt="Input preview" />
                <pre>{{ attachment.value }}</pre>
              </template>
            </td>
            <td class="text-center">
              <template v-if="attachment.processedAttachmentType === 'OcrResult'">
                <image-lightbox :image-url="`${baseUrl}/order/api/parseorder/${attachment.id}/visualization/withauth/${parseOrderAuthKey}`" :preview-height="250"
                                alt="Visualization preview" />
              </template>
              <template v-else>
                Keine Visualisierung verfügbar
              </template>
            </td>
          </tr>
          <tr v-if="orderData.processedAttachments && orderData.processedAttachments.length > 2">
            <td colspan="3" class="text-center">
              <b-link @click="attachmentsExpanded = !attachmentsExpanded">
                <b-icon :icon="attachmentsExpanded ? 'chevron-up' : 'chevron-down'" />
                {{ attachmentsExpanded ? 'Weniger anzeigen' : `${orderData.processedAttachments.length - 2} weitere Anhänge anzeigen` }}
              </b-link>
            </td>
          </tr>
        </tbody>
      </table>

      <h4>Extrahierte Produktdaten</h4>
      <table class="table table-bordered">
        <thead>
          <tr>
            <th>Erkannt aus Eingangsdaten</th>
            <th>Resultat</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(product, index) in orderData.extractorResult.products" :key="index">
            <td>
              <div>Ids: {{ product.productIds.join(', ') }}</div>
              <div>Produktnamen: {{ product.productQuery }}</div>
              <div>Menge: {{ product.quantity }}</div>
            </td>
            <td>
              <div class="status-group">
                <score-display :score="product.score" :high-confidence-threshold="0.75" :medium-confidence-min="0.6" :medium-confidence-max="0.75"
                               high-confidence-text="Der Produktname oder die Mengenangabe wurde gut erkannt."
                               medium-confidence-text="Der Produktname oder die Mengenangabe könnte mit dem Kundenwunsch übereinstimmen, bitte Produkt überprüfen."
                               low-confidence-text="Der Produktname oder die Mengenangabe stimmt mit grosser Wahrscheinlichkeit nicht mit dem Kundenwunsch überein." :score-decimals="4"
                               :show-score="false"
                               :success-visible="false">
                </score-display>
                <template v-if="getProductSearchResult(product.id).source === 'Embedding'">
                  <score-display
                                 :score="getEmbeddingScore(product.id)"
                                 :high-confidence-threshold="0.75"
                                 :medium-confidence-min="0.6"
                                 :medium-confidence-max="0.75"
                                 high-confidence-text="Das gefundene Produkt stimmt mit dem Kundenwunsch überein."
                                 medium-confidence-text="Das gefundene Produkt könnte mit dem Kundenwunsch übereinstimmen, bitte Produkt überprüfen."
                                 low-confidence-text="Das gefundene Produkt stimmt mit grosser Wahrscheinlichkeit nicht mit dem Kundenwunsch überein."
                                 :score-decimals="4"
                                 :show-score="false"
                                 :success-visible="false">
                  </score-display>
                </template>
                <template v-else>
                  <score-display
                                 :score="getProductSearchResult(product.id).searchScore"
                                 :high-confidence-threshold="0.75"
                                 :medium-confidence-min="0.6"
                                 :medium-confidence-max="0.75"
                                 high-confidence-text="Das gefundene Produkt stimmt mit dem Kundenwunsch überein."
                                 medium-confidence-text="Das gefundene Produkt könnte mit dem Kundenwunsch übereinstimmen, bitte Produkt überprüfen."
                                 low-confidence-text="Das gefundene Produkt stimmt mit grosser Wahrscheinlichkeit nicht mit dem Kundenwunsch überein."
                                 :score-decimals="4"
                                 :show-score="false"
                                 :success-visible="false">
                  </score-display>
                </template>
                <template v-if="!getAddBasketItem(product.id).successful">
                  <status-badge
                                icon="b-x-circle-fill"
                                variant="danger"
                                title="Fehler beim Hinzufügen">
                    Das Produkt konnte nicht dem Warenkorb hinzugefügt werden. ({{ getBasketItemErrorMessage(product.id) }})
                  </status-badge>
                </template>
              </div>
              <template v-if="getAddBasketItem(product.id).successful">
                <div class="mt-3">Produkt <strong>{{ getBasketItemProductName(product.id) }}</strong> ({{ getBasketItemProductId(product.id)
                }}) dem
                  Warenkorb hinzugefügt.</div>
              </template>

            </td>
          </tr>
        </tbody>
      </table>

      <h4>Extrahierte Standortdaten</h4>
      <table class="table table-bordered">
        <tbody>
          <tr>
            <th>Erkannt aus Eingangsdaten</th>
            <td>
              <div v-if="orderData.extractorResult.location && orderData.extractorResult.location.locationIds">Ids: {{ orderData.extractorResult.location.locationIds.join(', ') }}</div>
              <div v-if="orderData.extractorResult.location">Standortname: {{ orderData.extractorResult.location.locationQuery }}</div>
            </td>
          </tr>
          <tr>
            <th>Resultat</th>
            <td>
              <div class="status-group mb-2">
                <score-display v-if="orderData.extractorResult.location" :score="orderData.extractorResult.location.score" :high-confidence-threshold="0.75" :medium-confidence-min="0.6"
                               :medium-confidence-max="0.75" high-confidence-text="Der Standort wurde gut erkannt."
                               medium-confidence-text="Die ausgelesenen Standort-Angaben könnten mit den Kundenangaben übereinstimmen, bitte Standort überprüfen."
                               low-confidence-text="Die ausgelesenen Standort-Angaben stimmen mit grosser Wahrscheinlichkeit nicht mit den Kundenangaben überein." :score-decimals="4"
                               :show-score="false"
                               :success-visible="false">
                </score-display>
                <status-badge v-else icon="b-x-circle-fill" variant="danger" title="Es wurde keine Standort-Angaben in den Eingangsdaten gefunden.">
                  Es wurde keine Standort-Angaben in den Eingangsdaten gefunden.
                </status-badge>
                <status-badge v-if="(orderData.locations || []).length === 0" icon="b-x-circle-fill" variant="danger" title="Es konnte kein Standort gefunden werden.">
                  Es konnte kein Standort gefunden werden.
                </status-badge>
              </div>
              <template v-if="(orderData.locations || []).length > 0 && getBestMatchingLocation && getBestMatchingLocation.value">
                Der Standort <strong>{{ getBestMatchingLocation.value.name }}</strong> ({{ getBestMatchingLocation.value.externalId }}) wurde zugewiesen.
              </template>
            </td>
          </tr>
        </tbody>
      </table>
    </template>
  </div>
</template>

<script>
import http, { instances } from '@/$plugins/http';
import Loader from '@/components/Loader';
import MixinEntityBase from '@/components/mixins/EntityBase';
import ImageLightbox from '@/components/order/orderly/ImageLightbox';
import ScoreDisplay from '@/components/order/orderly/ScoreDisplay';
import StatusBadge from '@/components/order/orderly/StatusBadge';
import TaskItem from '@/components/order/orderly/TaskItem';
import DOMPurify from 'dompurify';
import MarkdownIt from 'markdown-it';

export default {
  name: 'Orders.Detail',
  mixins: [MixinEntityBase],
  components: {
    Loader,
    ImageLightbox,
    ScoreDisplay,
    StatusBadge,
    TaskItem
  },
  data() {
    return {
      orderData: null,
      orderDataLoaded: false,
      templateAuthKey: null,
      parseOrderAuthKey: null,
      allOrderlyTasks: [], // Store all tasks, not just incomplete ones
      attachmentsExpanded: false,
      baseUrl: instances.servicePlatform.defaults.baseURL
    }
  },
  mounted() {
    this.$store.dispatch('orderparsedata/getByOrderId', { orderId: this.eParent.id })
      .then(response => {
        this.orderData = response.result
        this.orderDataLoaded = true
      })
      .then(() => {
        this.$store.dispatch('orderlytasks/getByOrderParseDataId', { orderParseDataId: this.orderData.id })
          .then(response => {
            this.allOrderlyTasks = response.result // Store all tasks
          })
      })
      .catch(error => {
        console.error(error)
      })

    // Get Auth-Keys for image-download
    http({
      method: 'get',
      url: '/order/api/Templates/authkey'
    }).then(response => {
      this.templateAuthKey = response.data
    })

    http({
      method: 'get',
      url: '/order/api/ParseOrder/authkey'
    }).then(response => {
      this.parseOrderAuthKey = response.data
    })
  },
  computed: {
    is() {
      return this.$store.getters[`${this.eParent.key}/is`]
    },
    entity() {
      return this.$store.getters[`${this.eParent.key}/getEntityUnwrapped`](this.eParent.id) || {}
    },
    orderlyTasks() {
      // Filter incomplete tasks reactively
      return this.allOrderlyTasks.filter(task => task.isCompleted === false)
    },
    renderedMessage() {
      if (!this.orderData || !this.orderData.message) {
        return ''
      }
      // Parse markdown and sanitize HTML to prevent XSS attacks
      const md = new MarkdownIt({
        breaks: true, // Convert line breaks to <br>
        html: true // Allow HTML tags
      })
      const rawHtml = md.render(this.orderData.message)
      return DOMPurify.sanitize(rawHtml)
    },
    getLowestProductConfidenceScore() {
      if (!this.orderData || !this.orderData.extractorResult || !this.orderData.extractorResult.products) {
        return 1.0 // Return high confidence if no data available
      }

      const products = this.orderData.extractorResult.products
      let lowestScore = 1.0

      products.forEach(product => {
        // Check extraction confidence score
        if (product.score !== undefined && product.score < lowestScore) {
          lowestScore = product.score
        }

        // Check search result confidence score
        const searchResult = this.getProductSearchResult(product.id)
        if (searchResult && searchResult.searchScore !== undefined && searchResult.searchScore < lowestScore) {
          lowestScore = searchResult.searchScore
        }

        // Check embedding score if applicable
        if (searchResult && searchResult.source === 'Embedding') {
          const embeddingScore = this.getEmbeddingScore(product.id)
          if (embeddingScore !== undefined && embeddingScore < lowestScore) {
            lowestScore = embeddingScore
          }
        }
      })

      return lowestScore
    },
    getLowestLocationConfidenceScore() {
      if (!this.orderData || !this.orderData.extractorResult || !this.orderData.extractorResult.location) {
        return 0.0 // Return zero confidence if no data available
      }

      const location = this.orderData.extractorResult.location
      return location.score
    },
    getBestMatchingLocation() {
      const locations = this.orderData.locations || []
      locations.sort((a, b) => b.searchScore - a.searchScore)
      return locations[0]
    },
    productTasks() {
      return this.orderlyTasks.filter(task => task.section === 'Products')
    },
    locationTasks() {
      return this.orderlyTasks.filter(task => task.section === 'Location')
    },
    visibleAttachments() {
      const attachments = this.orderData?.processedAttachments || [];
      if (this.attachmentsExpanded) {
        return attachments;
      }
      return attachments.slice(0, 2);
    },
    basketItemsByProductId() {
      if (!this.orderData || !this.orderData.addBasketItemResult) {
        return {};
      }
      const itemsMap = {};
      const addBasketItems = this.orderData.addBasketItemResult || [];
      addBasketItems.forEach(item => {
        itemsMap[item.extractId] = item;
      });
      return itemsMap;
    }
  },
  methods: {
    getAddBasketItem(referenceId) {
      // Use cached map for better performance
      const cachedItem = this.basketItemsByProductId[referenceId];
      if (cachedItem) {
        return cachedItem;
      }
      // Fallback for items not in cache
      return { successful: false, addActionResult: { product: { name: 'Unbekanntes Produkt' }, errorMessage: { 'de-CH': 'Unbekanntes Produkt' } } };
    },
    getBasketItemProductName(referenceId) {
      const item = this.getAddBasketItem(referenceId);
      return item && item.addActionResult && item.addActionResult.product && item.addActionResult.product.name ? item.addActionResult.product.name : '';
    },
    getBasketItemProductId(referenceId) {
      const item = this.getAddBasketItem(referenceId);
      return item && item.addActionResult && item.addActionResult.product && item.addActionResult.product.id ? item.addActionResult.product.id : '';
    },
    getBasketItemErrorMessage(referenceId) {
      const item = this.getAddBasketItem(referenceId);
      return item && item.addActionResult && item.addActionResult.errorMessage && item.addActionResult.errorMessage['de-CH'] ? item.addActionResult.errorMessage['de-CH'] : '';
    },
    getProductSearchResult(extractId) {
      const products = this.orderData.products || []
      const addBasketItemResults = this.orderData.addBasketItemResult || []

      const addBasketItemResult = addBasketItemResults.find(item => item.extractId === extractId)
      if (!addBasketItemResult) {
        return 0
      }

      const productSearchResults = products.find(pResults => pResults.find(p => p.value.id === addBasketItemResult.searchId))
      return productSearchResults.find(p => p.value.id === addBasketItemResult.searchId)
    },
    getEmbeddingScore(extractId) {
      const product = this.getProductSearchResult(extractId)
      if (!product || product.source !== 'Embedding') {
        return 0
      }

      const cosineSimilarity = product.searchScore - 1

      return cosineSimilarity
    },
    getProductDescription(extractId, searchId) {
      const products = this.orderData.extractorResult.products
      const product = products.find(p => p.id === extractId)

      if (!product) {
        return 'Unbekanntes Produkt'
      }

      return product.productQuery || product.productIds.join(', ')
    },
    onTaskResolved(taskId) {
      this.$store.dispatch('orderlytasks/getByOrderParseDataId', { orderParseDataId: this.orderData.id })
        .then(response => {
          this.allOrderlyTasks = response.result // Store all tasks - filtering is now reactive
        })
    }
  }
}
</script>

<style lang="scss" scoped>
.markdown-content {
  line-height: 1.6;

  ::v-deep {

    h1,
    h2,
    h3,
    h4,
    h5,
    h6 {
      margin-top: 1em;
      margin-bottom: 0.5em;
      font-weight: bold;
    }

    h1 {
      font-size: 2em;
    }

    h2 {
      font-size: 1.5em;
    }

    h3 {
      font-size: 1.25em;
    }

    h4 {
      font-size: 1.1em;
    }

    p {
      margin-bottom: 1em;
    }

    ul,
    ol {
      margin-left: 1.5em;
      margin-bottom: 1em;
    }

    li {
      margin-bottom: 0.25em;
    }

    code {
      background-color: #f4f4f4;
      padding: 0.2em 0.4em;
      border-radius: 3px;
      font-family: monospace;
      font-size: 0.9em;
    }

    pre {
      background-color: #f4f4f4;
      padding: 1em;
      border-radius: 5px;
      overflow-x: auto;
      margin-bottom: 1em;

      code {
        background-color: transparent;
        padding: 0;
      }
    }

    blockquote {
      border-left: 4px solid #ddd;
      padding-left: 1em;
      margin-left: 0;
      color: #666;
      font-style: italic;
    }

    a {
      color: #007bff;
      text-decoration: none;

      &:hover {
        text-decoration: underline;
      }
    }

    table {
      border-collapse: collapse;
      width: 100%;
      margin-bottom: 1em;

      th,
      td {
        border: 1px solid #ddd;
        padding: 0.5em;
        text-align: left;
      }

      th {
        background-color: #f4f4f4;
        font-weight: bold;
      }
    }

    hr {
      border: none;
      border-top: 1px solid #ddd;
      margin: 1.5em 0;
    }

    img {
      max-width: 100%;
      height: auto;
    }
  }
}
</style>
